/**
 * PriceLadder — every price point for a VIN on ONE axis.
 *
 * Nine markers on a single line collide if each caption just sits above its dot,
 * so captions are packed into stacked rows either side of the line: markers are
 * placed low-to-high and each takes the nearest free slot its label fits in,
 * with a leader line back to its dot. Colour carries the grouping (your ask /
 * wholesale / cohort / retail) that separate lanes used to carry.
 */
import React, { useLayoutEffect, useRef, useState } from 'react';
import type { PriceBand } from './priceRange';
import './PriceLadder.css';

export type PriceMarkerTone = 'ask' | 'market' | 'cohort' | 'retail';

export interface PriceMarker {
  key: string;
  label: string;
  value?: number | null;
  hint?: string;
  tone?: PriceMarkerTone;
}

export interface PriceLadderBand extends PriceBand {
  key: string;
  tone?: 'wholesale' | 'retail';
}

interface PriceLadderProps {
  markers: PriceMarker[];
  bands?: PriceLadderBand[];
  /** Colour key rendered under the axis. */
  legend?: { tone: PriceMarkerTone; label: string }[];
  emptyText?: string;
}

const fmtMoney = (v: number): string => `$${Math.round(v).toLocaleString()}`;

const usable = (v: number | null | undefined): v is number =>
  typeof v === 'number' && Number.isFinite(v) && v > 0;

// Caption metrics, used to pack labels without measuring every node.
const CHAR_LABEL = 5.9;
const CHAR_VALUE = 7.4;
const CAPTION_PAD = 14;
const ROW_H = 30;
const MAX_ROWS = 3;
const FALLBACK_WIDTH = 820;

interface Placed {
  marker: PriceMarker & { value: number };
  pct: number;
  side: 'above' | 'below';
  row: number;
}

export const PriceLadder: React.FC<PriceLadderProps> = ({
  markers,
  bands = [],
  legend = [],
  emptyText = 'No pricing available.',
}) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(FALLBACK_WIDTH);

  useLayoutEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const measure = () => setWidth(el.clientWidth || FALLBACK_WIDTH);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const present = markers
    .filter((m): m is PriceMarker & { value: number } => usable(m.value))
    .sort((a, b) => a.value - b.value);

  const liveBands = bands.filter((b) => usable(b.low) && usable(b.high));

  const values: number[] = present.map((m) => m.value);
  for (const b of liveBands) values.push(b.low as number, b.high as number);

  if (values.length === 0) return <div className="pl-empty">{emptyText}</div>;

  const rawMin = Math.min(...values);
  const rawMax = Math.max(...values);
  const pad = rawMax === rawMin ? Math.max(rawMax * 0.1, 500) : (rawMax - rawMin) * 0.12;
  const lo = rawMin - pad;
  const hi = rawMax + pad;
  const pct = (v: number) => ((v - lo) / (hi - lo)) * 100;

  // Greedy packing: walk markers left to right and drop each into the closest
  // free slot, so the common case (few markers) keeps every label beside the line.
  const occupied = new Map<string, number>();
  const placed: Placed[] = present.map((m) => {
    const p = pct(m.value);
    const centre = (p / 100) * width;
    const half =
      (Math.max(m.label.length * CHAR_LABEL, fmtMoney(m.value).length * CHAR_VALUE) +
        CAPTION_PAD) /
      2;

    let best: { side: 'above' | 'below'; row: number } | null = null;
    for (let row = 0; row < MAX_ROWS && !best; row++) {
      for (const side of ['above', 'below'] as const) {
        const key = `${side}:${row}`;
        if (centre - half >= (occupied.get(key) ?? -Infinity) + 6) {
          best = { side, row };
          break;
        }
      }
    }
    // Everything collided: take the emptiest slot rather than dropping the label.
    if (!best) {
      let min = Infinity;
      best = { side: 'below', row: MAX_ROWS - 1 };
      for (let row = 0; row < MAX_ROWS; row++) {
        for (const side of ['above', 'below'] as const) {
          const edge = occupied.get(`${side}:${row}`) ?? -Infinity;
          if (edge < min) {
            min = edge;
            best = { side, row };
          }
        }
      }
    }
    occupied.set(`${best.side}:${best.row}`, centre + half);
    return { marker: m, pct: p, side: best.side, row: best.row };
  });

  const rowsAbove =
    Math.max(-1, ...placed.filter((p) => p.side === 'above').map((p) => p.row)) + 1;
  const rowsBelow =
    Math.max(-1, ...placed.filter((p) => p.side === 'below').map((p) => p.row)) + 1;
  const topSpace = rowsAbove * ROW_H + 10;
  const bottomSpace = rowsBelow * ROW_H + 10;

  return (
    <div className="pl">
      <div
        className="pl-track"
        ref={trackRef}
        style={{ height: topSpace + bottomSpace }}
      >
        <div className="pl-line" style={{ top: topSpace - 1 }} />

        {liveBands.map((b, i) => (
          <div
            key={b.key}
            className={`pl-band pl-band--${b.tone ?? 'wholesale'}`}
            style={{
              left: `${pct(b.low as number)}%`,
              width: `${pct(b.high as number) - pct(b.low as number)}%`,
              top: topSpace - 1 + (i === 0 ? -9 : 5),
            }}
            title={
              b.label ??
              `Range ${fmtMoney(b.low as number)} – ${fmtMoney(b.high as number)}`
            }
          />
        ))}

        {placed.map(({ marker: m, pct: p, side, row }) => {
          const reach = 8 + row * ROW_H;
          return (
            <div
              key={m.key}
              className={`pl-marker pl-marker--${m.tone ?? 'market'}`}
              style={{ left: `${p}%` }}
            >
              <span
                className="pl-leader"
                style={
                  side === 'above'
                    ? { bottom: bottomSpace, height: reach }
                    : { top: topSpace, height: reach }
                }
              />
              <span
                className="pl-caption"
                title={m.hint}
                style={
                  side === 'above'
                    ? { bottom: bottomSpace + reach }
                    : { top: topSpace + reach }
                }
              >
                <span className={`pl-caption-label${m.hint ? ' pl-caption-label--help' : ''}`}>
                  {m.label}
                </span>
                <span className="pl-caption-value">{fmtMoney(m.value)}</span>
              </span>
              <span className="pl-dot" style={{ top: topSpace - 5.5 }} />
            </div>
          );
        })}
      </div>

      <div className="pl-axis">
        <span>{fmtMoney(lo)}</span>
        <span>{fmtMoney((lo + hi) / 2)}</span>
        <span>{fmtMoney(hi)}</span>
      </div>

      {legend.length > 0 && (
        <div className="pl-legend">
          {legend.map((l) => (
            <span className="pl-legend-item" key={l.tone}>
              <span className={`pl-legend-dot pl-legend-dot--${l.tone}`} />
              {l.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default PriceLadder;
